import { supabase } from '../lib/supabase'
import {
  FinanceiroPelArquivoJaProcessadoError,
  type BeginPelFolderInput,
  type CompletePelFolderInput,
  type FinanceiroPelStatementFile,
  type FinanceiroPelStatementFolder,
  type RegisterPelFileInput,
  type UpdatePelFileStatusInput,
} from '../types/financeiroExtratosConta'

const FOLDERS_TABLE = 'finan_pel_statement_folders'
const FILES_TABLE = 'finan_pel_statement_files'

/** Chave única do dia: YYYY/MM/DD */
export function pelFolderKey(year: string, month: string, day: string): string {
  return `${year}/${month}/${day}`
}

/** Chave única do arquivo: YYYY/MM/DD/conta/arquivo */
export function pelFileKey(
  year: string,
  month: string,
  day: string,
  account: string,
  fileName: string
): string {
  return `${year}/${month}/${day}/${account}/${fileName}`
}

export const financeiroExtratosContaService = {
  /** Arquivos com status=ok (não reprocessar). */
  async listArquivosProcessadosOk(): Promise<FinanceiroPelStatementFile[]> {
    const { data, error } = await supabase
      .from(FILES_TABLE)
      .select('*')
      .eq('status', 'ok')
      .order('processed_at', { ascending: false })

    if (error) throw error
    return (data ?? []) as FinanceiroPelStatementFile[]
  },

  async isArquivoProcessadoOk(
    year: string,
    month: string,
    day: string,
    account: string,
    fileName: string
  ): Promise<boolean> {
    const { data, error } = await supabase
      .from(FILES_TABLE)
      .select('id')
      .eq('year', year)
      .eq('month', month)
      .eq('day', day)
      .eq('account', account)
      .eq('file_name', fileName)
      .eq('status', 'ok')
      .maybeSingle()

    if (error) throw error
    return !!data
  },

  /**
   * Remove pastas `processing` órfãs do próprio usuário (ex.: crash antigo).
   * CASCADE apaga arquivos parciais dessa pasta.
   * Também limpa arquivos `processing` órfãos do usuário.
   */
  async cleanupIncompleteFolders(userId: string | null | undefined): Promise<number> {
    if (!userId) return 0

    const staleBefore = new Date(Date.now() - 60 * 60 * 1000).toISOString()

    const { data: staleFiles, error: filesError } = await supabase
      .from(FILES_TABLE)
      .delete()
      .eq('status', 'processing')
      .eq('processed_by', userId)
      .lt('processed_at', staleBefore)
      .select('id')

    if (filesError) throw filesError

    const { data, error } = await supabase
      .from(FOLDERS_TABLE)
      .delete()
      .eq('status', 'processing')
      .eq('processed_by', userId)
      .lt('processed_at', staleBefore)
      .select('id')

    if (error) throw error
    return (data?.length ?? 0) + (staleFiles?.length ?? 0)
  },

  /**
   * Abre ou retoma a pasta do dia (folder_key = YYYY/MM/DD) em status=processing.
   * Pastas complete podem ser reabertas para novos arquivos.
   */
  async beginOrResumeFolder(input: BeginPelFolderInput): Promise<FinanceiroPelStatementFolder> {
    const year = input.year.trim()
    const month = input.month.trim()
    const day = input.day.trim()
    if (!year || !month || !day) {
      throw new Error('Ano, mês e dia são obrigatórios.')
    }

    const folderKey = pelFolderKey(year, month, day)

    const { data: existing, error: findError } = await supabase
      .from(FOLDERS_TABLE)
      .select('*')
      .eq('folder_key', folderKey)
      .maybeSingle()

    if (findError) throw findError

    if (existing) {
      const { data: updated, error: updateError } = await supabase
        .from(FOLDERS_TABLE)
        .update({
          status: 'processing',
          root_folder_name: input.rootFolderName,
          processed_by: input.userId,
          processed_at: new Date().toISOString(),
        })
        .eq('id', existing.id)
        .select('*')
        .single()

      if (updateError) throw updateError
      return updated as FinanceiroPelStatementFolder
    }

    const { data: pasta, error: pastaError } = await supabase
      .from(FOLDERS_TABLE)
      .insert({
        folder_key: folderKey,
        year,
        month,
        day,
        root_folder_name: input.rootFolderName,
        files_ok: 0,
        files_error: 0,
        status: 'processing',
        processed_by: input.userId,
        processed_at: new Date().toISOString(),
      })
      .select('*')
      .single()

    if (pastaError) {
      if (pastaError.code === '23505') {
        const { data: raced, error: racedError } = await supabase
          .from(FOLDERS_TABLE)
          .select('*')
          .eq('folder_key', folderKey)
          .single()
        if (racedError) throw racedError
        return raced as FinanceiroPelStatementFolder
      }
      throw pastaError
    }

    return pasta as FinanceiroPelStatementFolder
  },

  /**
   * Cria/atualiza o registro do PDF (ex.: status=processing antes do webhook).
   * Bloqueia se já existir registro com status=ok.
   */
  async registerFile(input: RegisterPelFileInput): Promise<FinanceiroPelStatementFile> {
    const alreadyOk = await this.isArquivoProcessadoOk(
      input.year,
      input.month,
      input.day,
      input.account,
      input.fileName
    )
    if (alreadyOk) {
      throw new FinanceiroPelArquivoJaProcessadoError(
        pelFileKey(input.year, input.month, input.day, input.account, input.fileName)
      )
    }

    const { data: existing, error: findError } = await supabase
      .from(FILES_TABLE)
      .select('id, status')
      .eq('year', input.year)
      .eq('month', input.month)
      .eq('day', input.day)
      .eq('account', input.account)
      .eq('file_name', input.fileName)
      .maybeSingle()

    if (findError) throw findError

    const payload = {
      folder_id: input.folderId,
      year: input.year,
      month: input.month,
      day: input.day,
      account: input.account,
      file_name: input.fileName,
      status: input.status,
      error_message: input.errorMessage ?? null,
      processed_by: input.userId,
      processed_at: new Date().toISOString(),
    }

    if (existing) {
      const { data, error } = await supabase
        .from(FILES_TABLE)
        .update(payload)
        .eq('id', existing.id)
        .select('*')
        .single()
      if (error) throw error
      return data as FinanceiroPelStatementFile
    }

    const { data, error } = await supabase.from(FILES_TABLE).insert(payload).select('*').single()
    if (error) {
      if (error.code === '23505') {
        throw new FinanceiroPelArquivoJaProcessadoError(
          pelFileKey(input.year, input.month, input.day, input.account, input.fileName)
        )
      }
      throw error
    }
    return data as FinanceiroPelStatementFile
  },

  /** Atualiza status do arquivo após resposta do webhook. */
  async updateFileStatus(input: UpdatePelFileStatusInput): Promise<FinanceiroPelStatementFile> {
    const { data, error } = await supabase
      .from(FILES_TABLE)
      .update({
        status: input.status,
        error_message: input.errorMessage ?? null,
        processed_by: input.userId,
        processed_at: new Date().toISOString(),
      })
      .eq('id', input.fileId)
      .select('*')
      .single()

    if (error) throw error
    return data as FinanceiroPelStatementFile
  },

  async completeFolder(input: CompletePelFolderInput): Promise<void> {
    const { data: files, error: listError } = await supabase
      .from(FILES_TABLE)
      .select('status')
      .eq('folder_id', input.folderId)

    if (listError) throw listError

    const filesOk =
      files?.filter((f) => f.status === 'ok').length ?? input.arquivosOk
    const filesError =
      files?.filter((f) => f.status === 'error').length ?? input.arquivosErro

    const { error } = await supabase
      .from(FOLDERS_TABLE)
      .update({
        status: 'complete',
        files_ok: filesOk,
        files_error: filesError,
        processed_at: new Date().toISOString(),
      })
      .eq('id', input.folderId)

    if (error) throw error
  },
}
